import { useGetFoldersQuery, useGetWorkspacesQuery } from '@carboncopyco/data';
import {
  PlusOutlineIcon,
  SubdirectoryRightOutlineIcon,
} from '@carboncopyco/icons';
import type { DirectoryEntity } from '@carboncopyco/types';
import { Button, Loading, Modal, TextSubHeader } from '@carboncopyco/ui';
import { useFolderAnalytics } from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { parseNodeId } from '../BrowseTree';
import { FolderTree } from '../FolderTree';

type MoveAssetModalProps = {
  close: () => void;
  origin: {
    id: string;
    entity: DirectoryEntity;
    workspaceId: string;
  };
  onSubmit: (folderId: string, workspaceId: string) => void;
  blockOtherWorkspaces?: boolean;
  isLoading?: boolean;
  tooltipText?: string;
};

export const MoveAssetModal = ({
  close,
  origin,
  onSubmit,
  blockOtherWorkspaces,
  isLoading,
  tooltipText,
}: MoveAssetModalProps) => {
  const { organizationId } = useParams();
  const { trackFolderClickAssetMoveModal } = useFolderAnalytics(
    'move_file_to_folder_modal'
  );

  const { data: workspaces, isLoading: isLoadingWorkspaces } =
    useGetWorkspacesQuery({
      organizationId,
    });
  const { data: folders, isLoading: isLoadingFolders } = useGetFoldersQuery(
    {
      organizationId,
      includeShared: false,
    },
    { skip: !workspaces }
  );

  const [showNewFolderInput, setShowNewFolderInput] = useBoolean();
  const [selectedItemId, setSelectedItemId] = useState<string>();
  const [openItemId, onOpenItemId] = useState<string>();

  const selectedItem = selectedItemId && parseNodeId(selectedItemId);
  const isOriginSelected =
    selectedItemId &&
    selectedItem[0] === String(origin.id) &&
    selectedItem[3] === String(origin.workspaceId) &&
    selectedItem[1] === origin.entity;

  return (
    <Modal
      open
      onClose={close}
      title={
        <div className="flex items-center mb-2.5 gap-3">
          <SubdirectoryRightOutlineIcon color="#37363F" />
          <TextSubHeader label="Move to" />
        </div>
      }
    >
      <div className="flex flex-col gap-5 mt-5 w-[460px]">
        <div className="h-80 rounded-md border border-ds-natural-150 p-2 overflow-y-auto">
          <FolderTree
            workspaces={workspaces}
            folders={folders}
            isLoading={isLoadingWorkspaces || isLoadingFolders}
            actionOriginLocation={origin}
            selectedItemId={selectedItemId}
            onSelectedItemId={setSelectedItemId}
            openItemId={openItemId}
            onOpenItemId={onOpenItemId}
            showAddNewFolderInput={showNewFolderInput}
            closeAddNewFolderInput={setShowNewFolderInput.setFalse}
            blockOtherWorkspaces={blockOtherWorkspaces}
            tooltipText={tooltipText}
          />
        </div>
        <div className="flex items-center justify-between">
          <Button
            secondary
            link
            label="New folder"
            leftIcon={<PlusOutlineIcon />}
            onClick={() => {
              if (selectedItemId !== openItemId) {
                onOpenItemId(selectedItemId);
              }
              setShowNewFolderInput.setTrue();
            }}
            disabled={
              !selectedItemId ||
              parseNodeId(selectedItemId)[1] !== 'workspace' ||
              showNewFolderInput
            }
          />
          <div className="flex gap-2">
            <Button
              secondary
              label="Cancel"
              onClick={close}
            />
            <Button
              disabled={isLoading || isOriginSelected || showNewFolderInput}
              label="Move here"
              onClick={() => {
                const workspace = workspaces?.find(
                  (ws) => String(ws.id) === parseNodeId(selectedItemId)[3]
                );
                trackFolderClickAssetMoveModal({
                  workspaceId: workspace.id,
                  folderId: parseNodeId(selectedItemId)[0],
                  workspaceName: workspace?.workspaceName,
                  workspaceType: workspace?.workspaceType,
                });
                onSubmit(selectedItem[0], selectedItem[3]);
              }}
              icon={isLoading && <Loading color="text-ds-accent3-100" />}
              style={{ width: 100 }}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};
